// Creusage et cycle de vie des trous (mécanique signature du moteur).
// Aucun import externe, aucun RNG, aucune horloge : déterminisme total.
import { hasSupport, tileAt } from './physics'
import { crateAt, GameState, Hole, newHole } from './state'
import { REGROW_TICKS, TRAP_TICKS, SUB, T, cellIndex, colOf, rowOf } from './types'

// Case creusable côté `side` sous le creuseur, ou -1. Règles :
//  cible = (col+side, row+1) ; il faut le creuseur au sol (aligné, porté, pas en
//  chute), la case latérale (col+side, row) VIDE ou CORDE (un or/objet posé dessus ne
//  bloque pas : il tombera dans le trou ; la corde permet de creuser accroché) et la cible = Brick.
export function canDig(s: GameState, d: { x: number, y: number, falling?: boolean }, side: -1 | 1): number {
  if (d.x % SUB !== 0 || d.y % SUB !== 0) return -1
  if (d.falling || !hasSupport(s, d)) return -1
  const col = (d.x >> 3) + side, row = (d.y >> 3)
  // Latérale VIDE ou CORDE (21/07) : accroché à une corde, la case voisine est presque toujours
  // une autre case de corde -> elle ne bloque pas le geste (le trou s'ouvre dessous, la corde reste).
  const lat = tileAt(s, col, row)
  if (lat !== T.Empty && lat !== T.Rope) return -1
  // Un or ou un objet posé sur la brique ne bloque PLUS le creusage (règle
  // inversée le 11/07) : il tombera dans le trou via settleLoose.
  if (tileAt(s, col, row + 1) !== T.Brick) return -1
  return cellIndex(col, row + 1, s.cols)
}

// Stabilité d'une pièce/d'un objet posé en (col,row) : tient si sa propre case est
// une échelle ou une corde, ou si la tuile DESSOUS n'est pas vide (brique, béton,
// échelle, corde, glace, sable, téléporteur...). Le bas de grille porte toujours.
export function isStableForLoose(s: GameState, col: number, row: number): boolean {
  const own = tileAt(s, col, row)
  if (own === T.Ladder || own === T.ExitLadder || own === T.Rope) return true
  if (crateAt(s, col, row + 1)) return true // une caisse (Phase 4) porte l'or/l'objet comme un sol (sinon l'or la traverse -> inatteignable -> soft-lock)
  return tileAt(s, col, row + 1) !== T.Empty
}

// Gravité de l'or et des objets (11/07) : tout ce qui est posé tombe d'une case
// tant que ce n'est pas stable et que la case dessous est vide et inoccupée.
// Point fixe déterministe (l'ordre de traitement n'influe pas : une case occupée
// bloque, donc deux pièces s'empilent identiquement quel que soit l'ordre).
export function settleLoose(s: GameState): void {
  const occupied = (cell: number) => s.golds.includes(cell) || s.items.some(i => i.cell === cell)
  const landed: number[] = []
  let moved = true
  while (moved) {
    moved = false
    for (let i = 0; i < s.golds.length; i++) {
      const cell = s.golds[i]!
      const col = colOf(cell, s.cols), row = rowOf(cell, s.cols)
      const below = cellIndex(col, row + 1, s.cols)
      if (!isStableForLoose(s, col, row) && tileAt(s, col, row + 1) === T.Empty && !occupied(below)) {
        s.golds[i] = below
        if (!landed.includes(i)) landed.push(i)
        moved = true
      }
    }
    for (const it of s.items) {
      const col = colOf(it.cell, s.cols), row = rowOf(it.cell, s.cols)
      const below = cellIndex(col, row + 1, s.cols)
      if (!isStableForLoose(s, col, row) && tileAt(s, col, row + 1) === T.Empty && !occupied(below)) {
        it.cell = below
        moved = true
      }
    }
  }
  for (const i of landed) s.events.push({ type: 'goldDropped', cell: s.golds[i]! })
  if (landed.length > 0) s.golds.sort((a, b) => a - b)
}

// Brique -> trou ouvert : la case devient vide, on enregistre le trou (repousse
// programmée) et on émet l'événement `dig`. `regrowTo` = tuile qui repoussera à
// échéance (défaut Brick, back-compat des trous normaux ; le sol friable passe Crumble).
export function digAt(s: GameState, cell: number, regrowTo: T = T.Brick): void {
  s.terrain[cell] = T.Empty
  s.holes.push(newHole(cell, s.tick + REGROW_TICKS, regrowTo))
  s.events.push({ type: 'dig', cell })
  // DÉSEMMURER (31/07) : si un moine était EMMURÉ dans cette brique, la rouvrir le rend au monde.
  // Il redevient un simple captif du nouveau trou, avec une minuterie fraîche : rien de spécial à
  // écrire, c'est la mécanique ordinaire qui reprend. Vaut pour la pioche du joueur comme pour une
  // bombe ou le creusage du boss — c'est le même digAt, donc la même règle partout.
  for (const m of s.monks) {
    if (!m.emmure || m.trappedCell !== cell) continue
    m.emmure = false
    m.trappedUntil = s.tick + TRAP_TICKS
    s.events.push({ type: 'moineDesemmure', cell })
  }
}

// Sable -> vide DÉFINITIF (pas de repousse, donc pas de Hole). Utilisé en Task 10.
export function digSand(s: GameState, cell: number): void {
  s.terrain[cell] = T.Empty
  s.events.push({ type: 'digSand', cell })
}

// Étape de repousse pour le rendu : 0 = ouvert, 1 = repousse imminente (dernier
// tiers), 2 = fermé (repousse dépassée).
export function regrowStage(s: GameState, h: Hole): 0 | 1 | 2 {
  const left = h.regrowAt - s.tick
  return left <= 0 ? 2 : left < REGROW_TICKS / 3 ? 1 : 0
}
